import { render } from "preact";
import { AppLayout, Button, createAuth, Notice, PageHeader } from "../../ui/src/index.ts";
import { App } from "./app/App.tsx";
import { initApi, initAuth, sessionLost } from "./app/actions.ts";
import { initStore, initialState, type Config } from "./app/store.ts";
import type { Sample } from "./domain/sample.ts";

// The build copies the environment's config and content next to the bundle.
async function load<T>(path: string): Promise<T> {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`Couldn't load ${path}`);
  // SAFETY: studio/build.ts writes config.json and copies content/*.json from checked-in files of these shapes.
  return res.json() as Promise<T>;
}

/** Shown when Studio can't read its own config or content, before the app can start. */
function StartupFailed({ reason }: { reason: string }) {
  return <AppLayout product="Curator Studio" density="compact" navigation={[]}>
    <PageHeader title={<>Curator <strong>Studio</strong></>} />
    <Notice tone="danger" title="Studio couldn't start" action={<Button size="sm" onClick={() => location.reload()}>Try again</Button>}>
      {reason}. Try again in a moment.
    </Notice>
  </AppLayout>;
}

const root = document.getElementById("root");
if (!root) throw new Error("The Curator Studio page has no #root element.");
try {
  const [config, sample] = await Promise.all([load<Config>("/config.json"), load<Sample>("/content/sample.json")]);
  // Real sign-in where the environment configures AuthKit. Finishing the
  // redirect back from AuthKit happens inside createAuth, before the first render.
  const auth = config.authkit ? await createAuth(config.authkit, location.origin, sessionLost, "/#/") : null;
  initAuth(auth);
  initStore(initialState(config, sample, auth?.user() ?? null));
  initApi();
  render(<App />, root);
} catch (e) {
  render(<StartupFailed reason={e instanceof Error ? e.message : "Studio couldn't read its settings"} />, root);
}
